import { requestJson } from './http';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

function headers(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return requestJson<T>(`${API_BASE_URL}/voice-invitations${path}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  });
}

export interface DonorPolicyPresentation {
  policy_id: string;
  version: string;
  required: boolean;
}

export interface DonorInvitationPresentation {
  version: number;
  state: string;
  expires_at: string;
  inviter_display_name: string | null;
  listeners: Array<{ display_name: string }>;
  estimated_effort: {
    estimated_minutes: number;
    voice_sample_count: number;
    listener_clip_count_per_listener: number;
  };
  policies: Record<string, DonorPolicyPresentation>;
}

export interface DonorSessionView {
  version: number;
  session: {
    state: string;
    expires_at: string;
    idle_expires_at: string;
  };
  invitation: DonorInvitationPresentation;
}

async function publicDonorPost<T>(path: string, token: string): Promise<T> {
  return requestJson<T>(`${API_BASE_URL}/voice-invitations${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

export function previewDonorInvitation(
  token: string,
): Promise<DonorInvitationPresentation> {
  return publicDonorPost<DonorInvitationPresentation>('/preview', token);
}

export function exchangeDonorInvitation(token: string): Promise<DonorSessionView> {
  return publicDonorPost<DonorSessionView>('/exchange', token);
}

export function requestPublicDonorResumeLink(
  token: string,
): Promise<{ status: string }> {
  return publicDonorPost<{ status: string }>('/resume-link', token);
}

export function isVoiceInvitationCaptureUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    const url = new URL(value);
    const secureOrigin = url.protocol === 'https:'
      || (url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname));
    return secureOrigin
      && !url.username
      && !url.password
      && url.pathname === '/voice-invitation'
      && !url.search
      && url.hash.startsWith('#token=')
      && url.hash.length > '#token='.length;
  } catch {
    return false;
  }
}

export async function createVoiceInvitation(
  recipientEmail: string,
  householdId: string,
  listenerIds: string[]
): Promise<{
  invitation_id: string;
  state: string;
  delivery_status: string;
  capture_url?: string;
}> {
  return post<{
    invitation_id: string;
    state: string;
    delivery_status: string;
    capture_url?: string;
  }>('', {
    recipient_email: recipientEmail,
    household_id: householdId,
    listener_ids: listenerIds,
  });
}

export async function previewVoiceInvitation(token: string): Promise<{
  listeners: Array<{ listener_id: string; display_name: string }>;
}> {
  return post('/preview', { token });
}

export async function acceptVoiceInvitation(
  token: string,
  voiceName: string
): Promise<{ voice_id: string; voice_name: string }> {
  return post<{ voice_id: string; voice_name: string }>('/accept', { token, voice_name: voiceName });
}

export async function consentVoiceInvitation(
  token: string,
  legacyMarketing?: boolean,
): Promise<{ state: string; consent_event_count: number }> {
  const decisions = typeof legacyMarketing === 'boolean'
    ? {
      voice_donor: 'granted',
      terms: 'acknowledged',
      privacy: 'acknowledged',
      marketing: legacyMarketing ? 'granted' : 'declined',
    }
    : {
      voice_model_creation: 'granted',
      household_voice_share: 'granted',
      terms: 'acknowledged',
      privacy: 'acknowledged',
    };
  return post<{ state: string; consent_event_count: number }>('/consent', {
    token,
    decisions,
  });
}

export interface VoiceInvitationStatus {
  lifecycle_delivery?: import("../components/DeliveryLifecycleStatus").LifecycleDelivery[];
  invitation_id: string;
  recipient_email: string | null;
  state: string;
  consent_status: 'required' | 'active' | 'revoked';
  delivery_status: string;
  listeners: Array<{ listener_id: string; display_name: string }>;
  voice_name: string | null;
  donor_name: string | null;
  expires_at: string;
  created_at: string;
  is_expired: boolean;
  can_resend: boolean;
  can_revoke: boolean;
  can_invite_again: boolean;
  requires_updated_consent_form: boolean;
  can_create_updated_invitation: boolean;
  voice_sample_count: number;
  listener_completion_count: number;
  capture_stage: 'details' | 'consent' | 'voice_samples' | 'listener_clips' | 'complete' | 'building' | 'ready' | 'blocked';
  build_status: 'pending' | 'queue_pending' | 'building' | 'ready' | 'blocked' | 'failed';
}

export async function fetchVoiceInvitations(
  householdId: string
): Promise<VoiceInvitationStatus[]> {
  return requestJson<VoiceInvitationStatus[]>(
    `${API_BASE_URL}/voice-invitations?household_id=${encodeURIComponent(householdId)}`,
    { headers: headers() }
  );
}

export async function resendVoiceInvitation(invitationId: string): Promise<{
  invitation_id: string;
  state: string;
  delivery_status: string;
  capture_url?: string;
  expires_at: string;
}> {
  return post<{
    invitation_id: string;
    state: string;
    delivery_status: string;
    capture_url?: string;
    expires_at: string;
  }>(`/${encodeURIComponent(invitationId)}/resend`, {});
}

export async function createManualVoiceInvitationLink(invitationId: string): Promise<{
  invitation_id: string;
  state: string;
  delivery_status: string;
  capture_url: string;
  expires_at: string;
}> {
  return post<{
    invitation_id: string;
    state: string;
    delivery_status: string;
    capture_url: string;
    expires_at: string;
  }>(`/${encodeURIComponent(invitationId)}/manual-link`, {});
}

export async function replaceLegacyVoiceInvitation(invitationId: string): Promise<{
  invitation_id: string;
  replaces_invitation_id: string;
  state: string;
  delivery_status: string;
  capture_url?: string;
  expires_at: string;
}> {
  return post<{
    invitation_id: string;
    replaces_invitation_id: string;
    state: string;
    delivery_status: string;
    capture_url?: string;
    expires_at: string;
  }>(`/${encodeURIComponent(invitationId)}/replace`, {});
}

export async function cancelVoiceInvitation(
  invitationId: string
): Promise<{ invitation_id: string; state: string }> {
  return post<{ invitation_id: string; state: string }>(`/${encodeURIComponent(invitationId)}/cancel`, {});
}
